import { useCallback, useMemo } from 'react';
import { useMetricsRepository } from 'new/state/metrics/repository/useMetricsRepository';
import { Log, PaginatedLog } from '../types';
import { SortOrder, SORT_ORDER } from '../constants';

const sortLogsByTimestamp = (
  logs: PaginatedLog[],
  sortOrder: SortOrder = SORT_ORDER.OLDEST,
): PaginatedLog[] => {
  const sorted = [...logs].sort((a, b) => {
    const timestampA = a.timestamp ?? 0;
    const timestampB = b.timestamp ?? 0;

    if (sortOrder === SORT_ORDER.NEWEST) {
      return timestampB - timestampA;
    } else {
      return timestampA - timestampB;
    }
  });
  return sorted;
};

type Logs = Log[];
type PaginatedLogs = PaginatedLog[];
type Meta = {
  current_page: number;
  org_id: number;
  pages_count: number;
  run_id: number;
  total_count: number;
};

type FlowNodeId = string;
type FlowRunLogsWithFacetsDataType = {
  isLoading: boolean;
  error: boolean;
  logs: Record<FlowNodeId, Log[]>;
  total: number;
  size: number;
  hasMore: boolean;
  nextPageValues: [number, string];
  facets: Record<string, number>;
};

type GetFlowRunLogsResponseType = Record<string, { data: Logs; meta: Meta }>;

export type UseRepositoryResponseType = {
  getMetrics: () => any;
  getFlowRuns: () => Record<
    string,
    { data_sinks: any[]; data_sources: any[]; data_sets: any[] }
  > | null;
  getFlowRunLogsByFlowId(originNodeId: string): GetFlowRunLogsResponseType;
  getFlowRunsByRunId(runId: string): { data: Logs; meta: Meta };
  getFlowRunLogsPagedByFlowId(
    runId: string,
    nextPage?: number,
    sortOrder?: SortOrder,
  ): { data: PaginatedLogs; meta: Meta };
  getFlowRunLogsWithFacets(): FlowRunLogsWithFacetsDataType;
  getFlowRunLogsNextPageValues(): any | 'initial_request';
  getFlowRunLogsWithFacetsByFlowId(sortOrder?: SortOrder): Log[];
  getFlowRunLogsFacetsData(): Record<string, Array<any>>;
};

export const useRepository = (originNodeId: string): UseRepositoryResponseType => {
  const { data: metricsData } = useMetricsRepository();
  const { flowRunLogs, flowRunLogsPaged, flowRunSummary } = metricsData;

  const getMetrics = useCallback(() => metricsData, [metricsData]);

  const getFlowRuns = useCallback(() => {
    const runs = flowRunSummary[originNodeId];
    if (runs && runs.data) {
      return runs.data;
    }

    return null;
  }, [flowRunSummary, originNodeId]);

  const getFlowRunLogsByFlowId = useCallback(
    (originNodeId: string) => {
      return flowRunLogs[originNodeId];
    },
    [flowRunLogs, originNodeId],
  );

  const getFlowRunsByRunId = useCallback(
    (runId: string) => {
      return flowRunLogs[originNodeId]?.[runId] || { data: [], meta: {} };
    },
    [flowRunLogs, originNodeId],
  );

  const getFlowRunLogsPagedByFlowId = useCallback(
    (runId: string, nextPage?: number, sortOrder?: SortOrder) => {
      const runIdKey = runId ? runId : '0'; // '0' is the key for the historical logs
      const logsByPage = flowRunLogsPaged?.[originNodeId]?.[runIdKey] as {
        meta: Meta;
        pagesData: { [page: number]: Log[] };
      };

      const pagesData = logsByPage?.pagesData ?? {};

      const listOfAllLogs: PaginatedLogs = Object.keys(pagesData)
        .map(Number)
        .sort((a, b) => a - b)
        .flatMap((pageNum) =>
          pagesData[pageNum].map((log, idx) => ({
            ...log,
            _pageNumber: pageNum,
            _indexInPage: idx,
          })),
        );

      const sortedLogs = sortOrder ? sortLogsByTimestamp(listOfAllLogs, sortOrder) : listOfAllLogs;

      return { data: sortedLogs, meta: logsByPage?.meta ?? ({} as Meta) };
    },
    [flowRunLogsPaged, originNodeId],
  );

  const getFlowRunLogsWithFacets = useCallback(() => {
    const data = metricsData['flowRunLogsWithFacets'];
    return data as FlowRunLogsWithFacetsDataType;
  }, [metricsData]);

  const getFlowRunLogsFacetsData = useCallback(() => {
    const data = metricsData.flowRunLogsWithFacets.facets;
    return data as Record<string, Array<any>>;
  }, [metricsData.flowRunLogsWithFacets.facets]);

  const getFlowRunLogsNextPageValues = useCallback(() => {
    const nextPageValues = metricsData.flowRunLogsWithFacets.nextPageValues;
    return nextPageValues as [number, string] | null;
  }, [metricsData.flowRunLogsWithFacets.nextPageValues]);

  const getFlowRunLogsWithFacetsByFlowId = useCallback(
    (sortOrder?: SortOrder) => {
      const data = metricsData.flowRunLogsWithFacets.logs[originNodeId];
      const logs = (data as Log[]) ?? [];

      if (sortOrder && logs.length > 0) {
        return sortLogsByTimestamp(logs, sortOrder);
      }

      return logs;
    },
    [metricsData.flowRunLogsWithFacets.logs, originNodeId],
  );

  return useMemo(
    () => ({
      getMetrics,
      getFlowRuns,
      getFlowRunLogsByFlowId,
      getFlowRunsByRunId,
      getFlowRunLogsPagedByFlowId,
      getFlowRunLogsWithFacets,
      getFlowRunLogsNextPageValues,
      getFlowRunLogsWithFacetsByFlowId,
      getFlowRunLogsFacetsData,
    }),
    [
      getMetrics,
      getFlowRuns,
      getFlowRunLogsByFlowId,
      getFlowRunsByRunId,
      getFlowRunLogsPagedByFlowId,
      getFlowRunLogsWithFacets,
      getFlowRunLogsNextPageValues,
      getFlowRunLogsWithFacetsByFlowId,
      getFlowRunLogsFacetsData,
    ],
  );
};
